import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Zap } from '../utils/icons';
import { FaLinkedinIn } from 'react-icons/fa';
import { useI18n } from '../i18n';

const Home = () => {
  const { t } = useI18n();

  const fadeUp = {
    hidden: { opacity: 1, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
  };

  const metricsValue = t('home.hero.metrics');
  const signalRowsValue = t('home.hero.signalRows');
  const principlesValue = t('home.credibility.steps');
  const metrics = Array.isArray(metricsValue) ? metricsValue : [];
  const signalRows = Array.isArray(signalRowsValue) ? signalRowsValue : [];
  const principles = Array.isArray(principlesValue) ? principlesValue : [];

  const projects = [
    {
      logo: '/images/logos/crespoguard.png',
      title: t('home.projects.items.crespoguard.title'),
      description: t('home.projects.items.crespoguard.desc'),
      caseId: t('home.projects.items.crespoguard.caseId'),
      scope: t('home.projects.items.crespoguard.scope'),
      status: t('home.projects.status.active'),
      href: 'https://crespoguard.com',
    },
    {
      logo: '/images/logos/nomnomad.webp',
      title: t('home.projects.items.nomnomad.title'),
      description: t('home.projects.items.nomnomad.desc'),
      caseId: t('home.projects.items.nomnomad.caseId'),
      scope: t('home.projects.items.nomnomad.scope'),
      status: t('home.projects.status.wip'),
      href: 'https://nomnomad.ai',
    },
  ];

  return (
    <div className="pt-20 ccc-site">
      <section className="ccc-brief-hero">
        <div className="ccc-container py-16 md:py-24">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(22rem,0.58fr)] lg:items-end">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <p className="ccc-eyebrow mb-6">{t('home.hero.kicker')}</p>
              <p className="ccc-brief-brand mb-4">Crespo Cyber Consulting LLC</p>
              <h1 className="ccc-brief-title max-w-4xl">{t('home.hero.title')}</h1>
              <p className="ccc-brief-copy mt-6 max-w-2xl">
                {t('home.hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/services" className="ccc-primary-btn px-5 py-3">
                  <Zap className="h-5 w-5" />
                  {t('nav.services')}
                </Link>
                <a
                  href="https://www.linkedin.com/in/miguel083"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ccc-outline-btn bg-white/60"
                >
                  <FaLinkedinIn className="h-4 w-4" />
                  {t('home.cta.connect')}
                </a>
              </div>
            </motion.div>

            <motion.aside
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="ccc-ledger-dark p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="ccc-label mb-2">{t('home.hero.signalKicker')}</p>
                  <h2 className="text-2xl font-black leading-tight">{t('home.hero.signalTitle')}</h2>
                </div>
                <div className="ccc-dossier-badge ccc-logo-badge" role="img" aria-label="Crespo Cyber Consulting logo">
                  <img src="/images/badges/crespo-mark.png" alt="" className="ccc-logo-badge-image" />
                </div>
              </div>
              <div className="mt-5">
                {signalRows.map((row) => (
                  <div key={row.label} className="ccc-ledger-row">
                    <span className="ccc-label">{row.label}</span>
                    <span className="ccc-value">{row.value}</span>
                  </div>
                ))}
              </div>
              {metrics.length > 0 && (
                <div className="mt-6 grid grid-cols-3 gap-3 border-t border-white/10 pt-5">
                  {metrics.map((metric) => (
                    <div key={metric.label}>
                      <p className="ccc-label">{metric.label}</p>
                      <p className="mt-2 text-sm font-black text-white">{metric.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.aside>
          </div>
        </div>
      </section>

      <section className="ccc-section">
        <div className="ccc-container">
          <div className="ccc-section-head">
            <div>
              <p className="ccc-eyebrow mb-4">{t('home.credibility.serviceArea')}</p>
              <h2 className="ccc-section-title">{t('home.credibility.title')}</h2>
            </div>
            <p className="ccc-section-copy">{t('home.credibility.subtitle')}</p>
          </div>

          <div className="ccc-scope-grid">
            {principles.map((step, index) => (
              <motion.article
                key={step.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={fadeUp}
                className="ccc-scope-cell"
              >
                <p className="ccc-scope-number">{String(index + 1).padStart(2, '0')}</p>
                <h3 className="ccc-scope-title">{step.title}</h3>
                <p className="ccc-scope-copy">{step.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccc-section bg-[#111816] text-white">
        <div className="ccc-container">
          <div className="ccc-section-head">
            <div>
              <p className="ccc-kicker mb-4">{t('home.projects.kicker')}</p>
              <h2 className="text-4xl font-black leading-none md:text-6xl">{t('home.projects.title')}</h2>
            </div>
            <p className="text-slate-300 leading-relaxed">{t('home.projects.subtitle')}</p>
          </div>

          <div className="grid gap-5">
            {projects.map((project) => (
              <motion.article
                key={project.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                className="ccc-product-panel bg-white text-slate-900"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="ccc-pill">{project.caseId}</span>
                    <span className="ccc-pill">{project.status}</span>
                  </div>
                  <h3 className="mt-6 text-3xl font-black">{project.title}</h3>
                  <p className="ccc-work-copy mt-4 max-w-2xl">{project.description}</p>
                  <div className="mt-7 grid gap-3 border-t border-slate-200 pt-5 sm:grid-cols-2">
                  <div>
                      <p className="ccc-label">{t('ui.scope')}</p>
                      <p className="ccc-value mt-2">{project.scope}</p>
                    </div>
                    <a
                      href={project.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ccc-link self-end text-sm"
                    >
                      {t('home.projects.visitSite')}
                    </a>
                  </div>
                </div>
                <div className="ccc-logo-tile">
                  <img src={project.logo} alt={project.title} className="max-h-24 max-w-[12rem] object-contain" />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccc-section-tight">
        <div className="ccc-container">
          <div className="ccc-ledger p-6 md:p-8">
            <div className="grid gap-8 md:grid-cols-[minmax(0,0.75fr)_auto] md:items-center">
              <div>
                <p className="ccc-eyebrow mb-4">{t('home.cta.kicker')}</p>
                <h2 className="text-3xl font-black text-slate-950 md:text-5xl">{t('home.cta.title')}</h2>
                <p className="ccc-section-copy mt-4 max-w-2xl">{t('home.cta.subtitle')}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link to="/projects" className="ccc-outline-btn bg-white">
                  <ShieldCheck className="h-4 w-4" />
                  {t('home.cta.viewAllProjects')}
                </Link>
                <a
                  href="https://www.linkedin.com/in/miguel083"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ccc-primary-btn px-5 py-3"
                >
                  <FaLinkedinIn className="h-4 w-4" />
                  {t('home.cta.connect')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
